import { createTheme, hexToRgb } from "@mui/material";
import { grey } from "@mui/material/colors";
import type {} from "@mui/x-data-grid/themeAugmentation";
import { hexToHSLA } from "@/utils/colors.ts";

export const getTheme = () => {
  return createTheme({
    colorSchemes: {
      light: {
        palette: {
          primary: {
            main: "#7367F0",
            light: "#8f84e8",
            dark: "#675DD8",
            contrastText: "#FAF9F6",
          },
          secondary: {
            main: "#808390",
            light: "#999CA6",
            dark: "#737682",
            contrastText: "#FFF",
          },
          error: {
            main: "#FF4C51",
            light: "#FF7074",
            dark: "#E64449",
            contrastText: "#FFF",
          },
          warning: {
            main: "#FF9F43",
            light: "#FFB269",
            dark: "#E68F3C",
            contrastText: "#FFF",
          },
          info: {
            main: "#00BAD1",
            light: "#33C8DA",
            dark: "#00A7BC",
            contrastText: "#FFF",
          },
          success: {
            main: "#28C76F",
            light: "#53D28C",
            dark: "#24B364",
            contrastText: "#FFF",
          },
          text: {
            primary: grey[900],
            secondary: grey[600],
            disabled: `rgb(var(--mui-mainColorChannels-light) / 0.4)`,
            primaryChannel: "var(--mui-mainColorChannels-light)",
            secondaryChannel: "var(--mui-mainColorChannels-light)",
          },
          background: {
            default: "#F8F7FA",
            paper: "#FFFFFF",
            paperChannel: "255 255 255",
          },
          action: {
            active: `rgb(var(--mui-mainColorChannels-light) / 0.6)`,
            hover: `rgb(var(--mui-mainColorChannels-light) / 0.06)`,
            selected: `rgb(var(--mui-mainColorChannels-light) / 0.08)`,
            disabled: `#C0C0C0`,
            disabledBackground: `rgb(var(--mui-mainColorChannels-light) / 0.16)`,
            focus: `rgb(var(--mui-mainColorChannels-light) / 0.1)`,
            focusOpacity: 0.1,
            activeChannel: "var(--mui-mainColorChannels-light)",
            selectedChannel: "var(--mui-mainColorChannels-light)",
          },
          Alert: {
            errorColor: "var(--mui-palette-error-main)",
            warningColor: "var(--mui-palette-warning-main)",
            infoColor: "var(--mui-palette-info-main)",
            successColor: "var(--mui-palette-success-main)",
            errorStandardBg: "var(--mui-palette-error-lightOpacity)",
            warningStandardBg: "var(--mui-palette-warning-lightOpacity)",
            infoStandardBg: "var(--mui-palette-info-lightOpacity)",
            successStandardBg: "var(--mui-palette-success-lightOpacity)",
            errorFilledColor: "var(--mui-palette-error-contrastText)",
            warningFilledColor: "var(--mui-palette-warning-contrastText)",
            infoFilledColor: "var(--mui-palette-info-contrastText)",
            successFilledColor: "var(--mui-palette-success-contrastText)",
            errorFilledBg: "var(--mui-palette-error-main)",
            warningFilledBg: "var(--mui-palette-warning-main)",
            infoFilledBg: "var(--mui-palette-info-main)",
            successFilledBg: "var(--mui-palette-success-main)",
          },
          Avatar: {
            defaultBg: "#EEEDF0",
          },
          Chip: {
            defaultBorder: "var(--mui-palette-divider)",
          },
          FilledInput: {
            bg: "var(--mui-palette-action-hover)",
            hoverBg: "var(--mui-palette-action-selected)",
            disabledBg: "var(--mui-palette-action-hover)",
          },
          SnackbarContent: {
            bg: "#2F2B3D",
            color: "var(--mui-palette-background-paper)",
          },
          Switch: {
            defaultColor: "var(--mui-palette-common-white)",
            defaultDisabledColor: "var(--mui-palette-common-white)",
            primaryDisabledColor: "var(--mui-palette-common-white)",
            secondaryDisabledColor: "var(--mui-palette-common-white)",
            errorDisabledColor: "var(--mui-palette-common-white)",
            warningDisabledColor: "var(--mui-palette-common-white)",
            infoDisabledColor: "var(--mui-palette-common-white)",
            successDisabledColor: "var(--mui-palette-common-white)",
          },
          Tooltip: {
            bg: "#2F2B3D",
          },
          TableCell: {
            border: "var(--mui-palette-divider)",
          },
        },
      },
      dark: {
        palette: {
          primary: {
            main: "#003A61",
            dark: "#003356",
            light: "#00406C",
            contrastText: "#FAF9F6",
          },
          secondary: {
            main: "#AADDFF",
            dark: "#70B4E0",
            light: "#D4EEFF",
            contrastText: "#393636",
          },
          background: {
            default: "#00111C",
            paper: "#001523",
          },
          text: {
            primary: "#FAF9F6",
            secondary: "#F9F6EE",
          },
        },
      },
    },
    typography: {
      h3: {
        fontSize: "1.1rem",
        fontWeight: 400,
      },
      h2: {
        fontSize: "1.5rem",
        fontWeight: 500,
      },
      h1: {
        fontSize: "2rem",
        fontWeight: 600,
      },
      fontFamily: "Inter",
      body1: {
        fontSize: "0.9rem",
        fontWeight: 400,
      },
      body2: {
        fontSize: "0.9rem",
        fontWeight: 300,
      },
    },
    shape: {
      borderRadius: "6px",
    },
    shadows: [
      "none",
      "0px 2px 4px rgb(0, 0, 0, 0.1)",
      "0px 2px 4px rgb(0, 0, 0, 0.1)",
      "0px 2px 4px rgb(0, 0, 0, 0.1)",
      "0px 2px 4px rgb(0, 0, 0, 0.1)",
      "0px 2px 4px rgb(0, 0, 0, 0.1)",
      "0px 2px 4px rgb(0, 0, 0, 0.1)",
      "0px 2px 4px rgb(0, 0, 0, 0.1)",
      "0px 2px 4px rgb(0, 0, 0, 0.1)",
      "0px 2px 4px rgb(0, 0, 0, 0.1)",
      "0px 2px 4px rgb(0, 0, 0, 0.1)",
      "0px 2px 4px rgb(0, 0, 0, 0.1)",
      "0px 2px 4px rgb(0, 0, 0, 0.1)",
      "0px 2px 4px rgb(0, 0, 0, 0.1)",
      "0px 2px 4px rgb(0, 0, 0, 0.1)",
      "0px 2px 4px rgb(0, 0, 0, 0.1)",
      "0px 2px 4px rgb(0, 0, 0, 0.1)",
      "0px 2px 4px rgb(0, 0, 0, 0.1)",
      "0px 2px 4px rgb(0, 0, 0, 0.1)",
      "0px 2px 4px rgb(0, 0, 0, 0.1)",
      "0px 2px 4px rgb(0, 0, 0, 0.1)",
      "0px 2px 4px rgb(0, 0, 0, 0.1)",
      "0px 2px 4px rgb(0, 0, 0, 0.1)",
      "0px 2px 4px rgb(0, 0, 0, 0.1)",
      "0px 2px 4px rgb(0, 0, 0, 0.1)",
    ],
    components: {
      MuiTextField: {
        defaultProps: {
          size: "small",
        },
        styleOverrides: {
          root: ({ theme }) => ({
            "&:hover": {
              transition: theme.transitions.create(["border-color"], {
                duration: "10s",
              }),
            },
          }),
        },
      },
      MuiFormLabel: {
        styleOverrides: {
          root: ({ theme }) => ({
            color: theme.palette.secondary.light,
          }),
        },
      },
      MuiButton: {
        defaultProps: {
          disableElevation: true,
        },
        styleOverrides: {
          root: ({ theme, ownerState }) => {
            let buttonColor = ownerState.color ?? "primary";
            if (buttonColor === "inherit") {
              buttonColor = "primary";
            }

            const baseStyle = {
              textTransform: "none" as const,
              backgroundColor: "transparent",
              color: theme.palette[buttonColor].main,
              borderColor: theme.palette[buttonColor].light,
              "&:hover": {
                backgroundColor: theme.palette.divider,
              },
            };

            if (ownerState.variant === "tonal") {
              return {
                ...baseStyle,
                backgroundColor: hexToHSLA(
                  theme.palette[buttonColor].light,
                  0.3
                ),
                color: theme.palette[buttonColor].main,
                "&:hover": {
                  backgroundColor: hexToHSLA(
                    theme.palette[buttonColor].dark,
                    0.3
                  )
                }
              };
            }
            return baseStyle;
          },
          loadingIndicator: ({ theme }) => ({
            color: theme.palette.primary.main,
          }),
        },
        variants: [
          {
            props: { variant: "navigation" },
            style: ({ theme }) => ({
              backgroundColor: "transparent",
              color: theme.palette.primary.contrastText,
              border: "none",
              padding: "8px 16px",
              borderRadius: theme.shape.borderRadius,
              transition: theme.transitions.create(
                ["background-color", "color"],
                {
                  duration: theme.transitions.duration.short,
                }
              ),
              "&:hover": {
                backgroundColor: theme.palette.secondary.light,
                color: theme.palette.primary.contrastText,
                "& .MuiTypography-root": {
                  color: theme.palette.primary.contrastText,
                },
              },
            }),
          },
          {
            props: { variant: "contained" },
            style: ({ theme }) => ({
              backgroundColor: theme.palette.primary.main,
              color: theme.palette.primary.contrastText,
              "&:hover": {
                backgroundColor: theme.palette.primary.dark,
              },
            }),
          },
        ],
      },
      MuiBadge: {
        styleOverrides: {
          root: ({ theme, ownerState }) => {
            let badgeColor = ownerState.color ?? "primary";
            if (badgeColor === "default") {
              badgeColor = "primary";
            }

            if (ownerState.variant === "tonal") {
              return {
                padding: ownerState.size === "small" ? 4 : 6,
                fontSize: "0.8rem",
                borderRadius: 4,
                backgroundColor: hexToHSLA(
                  theme.palette[badgeColor].light,
                  0.3
                ),
                fontWeight: 500,
                color: theme.palette[badgeColor].main,
                "&:hover": {
                  backgroundColor: hexToHSLA(
                    theme.palette[badgeColor].dark,
                    0.3
                  )
                }
              }
            }
          }
        }
      },
      MuiLinearProgress: {
        styleOverrides: {
          root: {
            height: "2px",
            maxWidth: "65%",
            margin: "auto",
          },
        },
      },
      MuiMenu: {
        styleOverrides: {
          paper: {
            boxShadow: "0px 2px 8px rgba(0, 0, 0, 0.35)",
            marginTop: "4px",
          },
        },
      },
      MuiAutocomplete: {
        styleOverrides: {
          paper: {
            boxShadow: "0px 2px 8px rgba(0, 0, 0, 0.35)",
            marginTop: "4px",
          },
        },
      },
      MuiDivider: {
        styleOverrides: {
          root: ({ theme }) => ({
            color: theme.palette.divider,
          }),
        },
      },
      MuiChip: {
        styleOverrides: {
          root: ({ theme, ownerState }) => {
            if (ownerState.variant === "tonal") {
              const chipColor = ownerState.color ?? "primary";

              if (chipColor === "default") {
                return {
                  backgroundColor: hexToHSLA(
                    theme.palette.secondary.light,
                    0.3
                  ),
                  color: theme.palette.secondary.main,
                };
              }

              return {
                backgroundColor: hexToHSLA(theme.palette[chipColor].light, 0.3),
                color: theme.palette[chipColor].main,
              };
            }
          },
        },
      },
    },
  });
};

declare module "@mui/material/Button" {
  // eslint-disable-next-line
  interface ButtonPropsVariantOverrides {
    navigation: true;
  }
}

declare module "@mui/material/Chip" {
  // eslint-disable-next-line
  interface ChipPropsVariantOverrides {
    tonal: true;
  }
}

declare module "@mui/material/Button" {
  // eslint-disable-next-line
  interface ButtonPropsVariantOverrides {
    tonal: true;
  }
}

declare module "@mui/material/Badge" {
  // eslint-disable-next-line
  interface BadgePropsVariantOverrides {
    tonal: true;
  }
  // eslint-disable-next-line
  interface BadgeOwnProps {
    size?: "small" | "medium";
  }
}


declare module "@mui/material/TextField" {
  // eslint-disable-next-line
  interface TextFieldPropsVariantOverrides {
    filter: true;
  }
}
